import { Injectable } from "@angular/core";
import { ClusterJewelTieringMode } from "../../models/enums";
import { ClusterJewelBase, ClusterJewelTierValue, ClusterJewelTierValueList } from "../../models/item-data-models";
import { PricedClusterJewel } from "../../models/economy-models";
import { Filter } from "../../models/filter-models";
import { EconomyService } from "../economy.service";
import { logError } from "../../app.config";
import { getEffectiveTierThresholdValue } from "../../tier-list-tab/tier-value-helpers";

const SIZE_TO_BASE_TYPE: Record<string, string> = {
  'large': 'Large Cluster Jewel',
  'medium': 'Medium Cluster Jewel',
  'small': 'Small Cluster Jewel',
};

const CLUSTER_JEWEL_MIN_MAX_LEVEL_MAP: { [key: number]: number | undefined } = {
  1: 49,
  50: 67,
  68: 74,
  75: 83,
  84: undefined,
};

@Injectable({
  providedIn: 'root'
})
export class ClusterJewelTierService {
  private excellentClusterJewelBases: ClusterJewelBase[] = [];
  private goodClusterJewelBases: ClusterJewelBase[] = [];

  private tierCutoffValues: {
    mode: ClusterJewelTieringMode,
    values: ClusterJewelTierValueList
  }[] = [
    {
      mode: 'Normal',
      values: [
        { tier: 'Excellent', value: 75, baseCurrency: 'Chaos Orb' },
        { tier: 'Good', value: 25, baseCurrency: 'Chaos Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Chaos Orb' },
      ]
    },
    {
      mode: 'Strict',
      values: [
        { tier: 'Excellent', value: 250, baseCurrency: 'Chaos Orb' },
        { tier: 'Good', value: 100, baseCurrency: 'Chaos Orb' },
        { tier: 'Other', value: 0, baseCurrency: 'Chaos Orb' },
      ]
    }
  ];

  private clusterJewelTierValueList: ClusterJewelTierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;

  constructor(private economyService: EconomyService) {}

  setTieringMode(mode: ClusterJewelTieringMode, filter: Filter) {
    const valueList = mode === 'Custom' ? filter.clusterJewelTieringModeCustom : this.tierCutoffValues.find(t => t.mode === mode)?.values;
    if (!valueList) {
      logError('ClusterJewelTierService.setTieringMode: No value list found for mode ' + mode + '. Using default Normal.');
      this.clusterJewelTierValueList = this.tierCutoffValues.find(t => t.mode === 'Normal')!.values;
    } else {
      this.clusterJewelTierValueList = valueList;
    }
    filter.clusterJewelTieringMode = mode;
    this.buildTieredClusterJewels();
  }

  private buildTieredClusterJewels() {
    try {
      const clusterJewelData = this.economyService.getClusterJewelsData();

      const currentCurrencyData = this.economyService.getCurrencyData();
      const chaosValue = currentCurrencyData.find(item => item.name === 'Chaos Orb')?.price ?? 10;
      const divineValue = currentCurrencyData.find(item => item.name === 'Divine Orb')?.price ?? 200;

      const toExaltValue = (threshold: ClusterJewelTierValue): number => {
        return getEffectiveTierThresholdValue(threshold) * (threshold.baseCurrency === 'Chaos Orb' ? chaosValue : threshold.baseCurrency === 'Divine Orb' ? divineValue : 1);
      };

      const excellentThreshold = this.clusterJewelTierValueList.find(t => t.tier === 'Excellent');
      const goodThreshold = this.clusterJewelTierValueList.find(t => t.tier === 'Good');

      const excellentMinPrice = excellentThreshold ? toExaltValue(excellentThreshold) : 120 * chaosValue;
      const goodMinPrice = goodThreshold ? toExaltValue(goodThreshold) : 60 * chaosValue;

      const excellentItems: PricedClusterJewel[] = [];
      const goodItems: PricedClusterJewel[] = [];

      for (const jewel of clusterJewelData) {
        if (!jewel.price) continue;

        if (jewel.price >= excellentMinPrice) {
          excellentItems.push(jewel);
        } else if (jewel.price >= goodMinPrice) {
          goodItems.push(jewel);
        }
      }

      this.excellentClusterJewelBases = this.groupIntoClusterJewelBases(excellentItems);
      this.goodClusterJewelBases = this.groupIntoClusterJewelBases(goodItems);
    } catch (error) {
      logError('ClusterJewelTierService.buildTieredClusterJewels: Error building tiered cluster jewels: ' + error);
    }
  }

  private groupIntoClusterJewelBases(items: PricedClusterJewel[]): ClusterJewelBase[] {
    const groupKey = (item: PricedClusterJewel) =>
      `${item.size}|${item.passives}|${item.ilvl}`;

    const groups = new Map<string, { size: string, passives: number, ilvl: number, names: string[] }>();

    for (const item of items) {
      const key = groupKey(item);
      let group = groups.get(key);
      if (!group) {
        group = { size: item.size, passives: item.passives, ilvl: item.ilvl, names: [] };
        groups.set(key, group);
      }
      group.names.push(item.name);
    }

    return Array.from(groups.values()).map(group => ({
      baseType: SIZE_TO_BASE_TYPE[group.size] || group.size,
      minItemLevel: group.ilvl || undefined,
      maxItemLevel: CLUSTER_JEWEL_MIN_MAX_LEVEL_MAP[group.ilvl],
      passiveNum: group.passives || undefined,
      passiveNodes: group.names,
    }));
  }

  getExcellentClusterJewelBases(): ClusterJewelBase[] {
    return this.excellentClusterJewelBases;
  }

  getGoodClusterJewelBases(): ClusterJewelBase[] {
    return this.goodClusterJewelBases;
  }

  getSelectedTierCutoffValues(): ClusterJewelTierValueList {
    return this.clusterJewelTierValueList;
  }

  getTierCutoffValues() {
    return this.tierCutoffValues;
  }
}
